import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  MessageCircle,
  Mail,
  ArrowLeft,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/boost/Navbar";
import Footer from "@/components/boost/Footer";
import { BRAND, POST_REG_STEPS } from "@/constants/content";

const ThankYou = () => {
  const [params] = useSearchParams();
  const name = params.get("name") || "there";
  const pid = params.get("pid");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const whatsappUrl = process.env.REACT_APP_WHATSAPP_INVITE_URL || "";

  return (
    <div className="relative min-h-screen">
      <Helmet>
        <title>Seat Reserved — BoostF</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Navbar />

      <main className="pt-32 pb-24">
        <div className="container-x">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="mx-auto w-20 h-20 rounded-full grid place-items-center bg-gradient-to-br from-emerald-500 to-lime-400 text-white shadow-emerald-glow">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <div className="mt-6 chip mx-auto">
              <span className="live-dot" />
              Seat confirmed
            </div>

            <h1
              className="mt-4 font-display font-extrabold tracking-tight text-slate-900"
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
            >
              Welcome, {name}.{" "}
              <span className="gradient-text-emerald">
                Your seat is reserved.
              </span>
            </h1>

            <p className="mt-4 text-slate-600 leading-7">
             <span className="font-semibold text-emerald-700">
              Payment Successful!
             </span>{" "}
             Your seat in the <strong>BoostF eCommerce Launch Marathon </strong>
             has been confirmed. Click the button below to join the official WhatsApp Community where you'll receive the marathon date, webinar links, resources and all future announcements.
            </p>

            {pid && (
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 border border-slate-200 font-mono text-xs text-slate-700">
                <span>Payment ID: {pid}</span>

                <button
                  onClick={() => {
                    navigator.clipboard.writeText(pid);
                    toast.success("Payment ID copied");
                  }}
                  aria-label="Copy payment id"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </motion.div>

          {/* WHAT HAPPENS NEXT */}

          <div className="mt-12 max-w-3xl mx-auto rounded-3xl bg-white border border-slate-100 shadow-premium p-6 sm:p-8">

            <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-700">
              What happens next
            </div>

            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {POST_REG_STEPS.map((s) => (
                <li
                  key={s.step}
                  className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100 bg-slate-50/50"
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-500 text-white grid place-items-center font-display font-bold text-xs">
                    {s.step}
                  </div>

                  <div className="text-sm text-slate-800 font-medium">
                    {s.label}
                  </div>
                </li>
              ))}
            </ul>

            {/* WhatsApp Community Card */}

            {/* Community CTA */}

          <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 text-center">
          
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-700">
              NEXT STEP
            </div>
          
            <h3 className="mt-4 text-2xl font-bold text-slate-900">
              Join the Official BoostF WhatsApp Community
            </h3>
          
            <p className="mt-3 text-slate-600 max-w-lg mx-auto">
              All webinar announcements, marathon date, joining instructions,
              session links and future updates will be shared inside the official
              BoostF WhatsApp Community.
            </p>
          
            <div className="mt-6">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center h-14 text-base font-semibold"
              >
                <MessageCircle className="w-5 h-5" />
                Join Official WhatsApp Community
              </a>
            </div>
          
            <p className="mt-4 text-xs text-slate-500">
              After joining, keep notifications enabled so you don't miss any important updates.
            </p>
          
            <div className="mt-5 flex justify-center">
              <a
                href={`mailto:${BRAND.supportEmail}`}
                className="btn-ghost justify-center"
              >
                <Mail className="w-4 h-4" />
                Contact Support
              </a>
            </div>
          
          </div>

          </div>

          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ThankYou;